import React from 'react'
import classNames from 'classnames'
import { When } from 'react-if'
import LazyHydrate from 'react-lazy-hydration'
import { PaddedContainer } from '@/atoms/PaddedContainer'
import { useProjectMeta } from '@/contexts/ProjectMetaContext'
import { ClaimContextProvider } from '@/organisms/ClaimCollectibleButton/ClaimContext'
import { HelpCastingButton } from '@/organisms/HelpCastingButton'
import { HorizontalShopifyMerchScroller } from '@/organisms/HorizontalShopifyMerchScroller'
import { Livestream, LivestreamCtaButton, LivestreamProvider } from '@/organisms/Livestream'
import { LivestreamBreadcrumb } from '@/organisms/LivestreamBreadcrumb'
import { LivestreamInformation } from '@/organisms/LivestreamInformation'
import { WatchPartyChat } from '@/organisms/WatchPartyChat'
import { LivestreamCta } from '@/services/CtaService'
import { LivestreamMeta } from '@/services/LivestreamService'
import type { ProjectMeta } from '@/services/ProjectsService'
import { useSafeTrack } from '@/utils/analytics'

interface LivestreamViewProps {
  streamId: string
  livestreamMeta: LivestreamMeta
  projectMeta: ProjectMeta
  livestreamCta: LivestreamCta | null
}

export const LivestreamView: React.FC<LivestreamViewProps> = ({
  streamId,
  livestreamMeta,
  projectMeta,
  livestreamCta,
}) => {
  const track = useSafeTrack()
  const { shopifyStore, slug } = useProjectMeta()
  const dayOfLivestream = Boolean(new Date(livestreamMeta.startAt).getDate() === new Date().getDate())

  const handleDesktopHelpCastingClick = () => {
    track('Livestream Desktop Help Casting Clicked')
  }

  const handleMobileHelpCastingClick = () => {
    track('Livestream Mobile Help Casting Clicked')
  }

  return (
    <ClaimContextProvider>
      <div className="overflow-x-hidden bg-core-gray-950 pb-10 pt-28 text-white">
        <LivestreamProvider streamId={streamId} projectSlug={projectMeta.slug}>
          <PaddedContainer fluid>
            <div className="hidden gap-4 md:mb-4 md:flex md:items-center md:justify-between">
              <LivestreamBreadcrumb
                livestreamTitle={livestreamMeta.name}
                projectName={projectMeta.name}
                projectSlug={projectMeta.slug}
              />
              <div className="flex flex-row gap-2 md:gap-4">
                <When condition={dayOfLivestream}>
                  <div className="hidden lg:block">
                    <HelpCastingButton onClick={handleDesktopHelpCastingClick} />
                  </div>
                </When>
                <LivestreamCtaButton projectSlug={projectMeta.slug} livestreamCta={livestreamCta} guid={streamId} />
              </div>
            </div>
            <div className="flex flex-col flex-wrap gap-4 lg:flex-row lg:flex-nowrap">
              <main
                className={classNames(
                  'relative w-full shrink-0',
                  dayOfLivestream && 'lg:w-[620px] xl:w-[720px] 2xl:w-[70%]',
                )}
              >
                <Livestream livestreamMeta={livestreamMeta} streamId={streamId} />
              </main>
              <When condition={!livestreamMeta.unavailableReason}>
                <div className="flex w-full justify-between gap-x-4 md:hidden">
                  <LivestreamCtaButton projectSlug={projectMeta.slug} guid={streamId} livestreamCta={livestreamCta} />
                </div>
              </When>
              <div className="flex w-full flex-col gap-4 md:hidden">
                <HelpCastingButton onClick={handleMobileHelpCastingClick} />
              </div>
              <When condition={dayOfLivestream}>
                <div className="h-96 w-full grow-1 lg:h-[60vh] lg:w-auto">
                  <WatchPartyChat />
                </div>
              </When>
            </div>
            <div className="mb-8 w-full py-8 pr-8">
              <LazyHydrate whenVisible>
                <LivestreamInformation
                  livestreamMeta={livestreamMeta}
                  projectMeta={projectMeta}
                  hideShare={livestreamMeta?.whitelistedGroups?.length > 0}
                />
              </LazyHydrate>
            </div>
          </PaddedContainer>
          {shopifyStore && (
            <LazyHydrate whenVisible>
              <HorizontalShopifyMerchScroller
                storeUrl={shopifyStore.storeUrl}
                accessToken={shopifyStore.accessToken}
                handle={shopifyStore.handle}
                storeHref={shopifyStore?.storeHref}
                analytics={{
                  program: slug,
                  context: 'early-access',
                }}
                slug={slug}
                padding
              />
            </LazyHydrate>
          )}
        </LivestreamProvider>
      </div>
    </ClaimContextProvider>
  )
}
